/**
 * Authentication Service - Centralized authentication logic
 */

import {
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  updatePassword,
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  User,
  AuthError,
  sendEmailVerification
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp, Timestamp, FieldValue } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { sessionSecurity } from '../utils/sessionSecurity';
import { appConfig } from '../config/appConfig';
import { monitoring } from '../utils/monitoring';
import { callFunction } from '../utils/functions';
import { logger } from '../utils/logger';
import { getErrorMessage } from '../utils/errorHandler';

export interface UserProfile {
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  createdAt?: Timestamp | FieldValue;
  lastLoginAt?: Timestamp | FieldValue;
  emailVerified?: boolean;
  role?: 'user' | 'admin';
  subscription?: 'free' | 'pro' | 'enterprise';
  preferences?: UserPreferences;
}

export interface UserPreferences {
  theme?: 'light' | 'dark';
  language?: string;
  notifications?: boolean;
  autoSave?: boolean;
}

export interface AuthResult {
  success: boolean;
  user?: User;
  error?: string;
  code?: string;
}

class AuthService {
  private currentUser: User | null = null;

  constructor() {
    // Listen to auth state changes
    auth.value.onAuthStateChanged((user) => {
      this.currentUser = user;
      if (user) {
        monitoring.setUserId(user.uid);
        this.updateLastLogin(user.uid);
      } else {
        // S232: errors after sign-out were tagged with the previous child's uid.
        monitoring.setUserId('');
      }
    });
  }

  /**
   * NOTE (2026-09-05): `register` and its `createUserProfile` helper were
   * REMOVED from this service.
   *
   * They were a second registration path, unused by any component — the real
   * one is `AuthContext.register` — and they did not collect a birth date. The
   * COPPA gate in `firestore.rules` applies only to accounts flagged
   * `requiresParentalConsent`, which is derived from that date at registration
   * and is immutable afterwards. So any account created through here would have
   * been permanently outside the consent gate: publishing and messaging
   * ungated, with nothing to reveal it. Whoever wired it up next would have
   * introduced that silently.
   *
   * They also wrote `role: 'user'` and `subscription: 'free'`. `'user'` is not
   * one of the roles in `types/roles.ts` (student / teacher / parent / admin /
   * district_admin / super_admin), so `roleService.getUserRole` treated it as
   * corrupt and fell back to `student`. Two role vocabularies, one of them
   * fictional.
   */

  /**
   * Sign in a user
   */
  async signIn(email: string, password: string): Promise<AuthResult> {
    try {
      // Rate limiting check (basic client-side)
      const lastAttempt = sessionStorage.getItem('last_login_attempt');
      if (lastAttempt) {
        const timeSinceLastAttempt = Date.now() - parseInt(lastAttempt);
        if (timeSinceLastAttempt < 1000) { // 1 second minimum between attempts
          return { success: false, error: 'Too many attempts. Please wait.' };
        }
      }
      sessionStorage.setItem('last_login_attempt', Date.now().toString());

      const credential = await signInWithEmailAndPassword(auth.value, email, password);
      const user = credential.user;

      // S135: this read `process.env.REACT_APP_REQUIRE_EMAIL_VERIFICATION ===
      // 'true'` directly while `appConfig` computes the same flag as opt-OUT
      // (S128) and forces it on in production. So the two disagreed in exactly
      // the dangerous direction: a production build with the variable unset had
      // `appConfig.isFeatureEnabled('requireEmailVerification') === true` — an
      // audit reading the config would report verification as required — while
      // this line signed the unverified user straight in. One flag, one reader.
      if (!user.emailVerified && appConfig.isFeatureEnabled('requireEmailVerification')) {
        // S135: and the block was a dead end. Nothing ever sent a first
        // verification email (registration did not, and still would not until
        // this change's other half), and `resendEmailVerification` needs a
        // signed-in user — which this sign-out immediately removes. Turning the
        // flag on therefore locked every new account out permanently, with the
        // error politely telling them to check an inbox nothing had written to.
        // Send one before letting go of the session.
        try {
          await sendEmailVerification(user);
        } catch (sendError) {
          logger.error('Could not send a verification email during login', sendError);
        }
        await signOut(auth.value);
        return {
          success: false,
          error: 'Please verify your email before signing in. We have sent you a new link.'
        };
      }

      // Update last login
      await this.updateLastLogin(user.uid);

      // Log successful login
      monitoring.trackEvent('user_signed_in', { userId: user.uid });

      return { success: true, user };
    } catch (error) {
      const authError = error as AuthError;
      monitoring.logError(
        new Error(authError.message), 
        undefined, 
        { code: authError.code }
      );
      
      return { 
        success: false, 
        error: this.getErrorMessage(authError.code),
        code: authError.code 
      };
    }
  }

  /**
   * Sign out the current user
   */
  async signOutUser(): Promise<AuthResult> {
    try {
      await signOut(auth.value);
      sessionSecurity.secureLogout();
      monitoring.trackEvent('user_signed_out', { userId: this.currentUser?.uid });
      return { success: true };
    } catch (error) {
      const authError = error as AuthError;
      return { 
        success: false, 
        error: this.getErrorMessage(authError.code),
        code: authError.code 
      };
    }
  }

  /**
   * Send password reset email
   */
  async resetPassword(email: string): Promise<AuthResult> {
    try {
      await sendPasswordResetEmail(auth.value, email);
      monitoring.trackEvent('password_reset_requested', { email });
      return { success: true };
    } catch (error) {
      const authError = error as AuthError;
      return { 
        success: false, 
        error: this.getErrorMessage(authError.code),
        code: authError.code 
      };
    }
  }

  /**
   * Update user password
   */
  async updateUserPassword(currentPassword: string, newPassword: string): Promise<AuthResult> {
    try {
      if (!this.currentUser || !this.currentUser.email) {
        return { success: false, error: 'No user is currently signed in' };
      }

      // Validate new password
      if (!this.validatePassword(newPassword)) {
        return { 
          success: false, 
          error: 'New password must be at least 8 characters with uppercase, lowercase, and number' 
        };
      }

      // Re-authenticate user
      const credential = EmailAuthProvider.credential(
        this.currentUser.email,
        currentPassword
      );
      await reauthenticateWithCredential(this.currentUser, credential);

      // Update password
      await updatePassword(this.currentUser, newPassword);
      
      monitoring.trackEvent('password_updated', { userId: this.currentUser.uid });
      
      return { success: true };
    } catch (error) {
      const authError = error as AuthError;
      return { 
        success: false, 
        error: this.getErrorMessage(authError.code),
        code: authError.code 
      };
    }
  }

  /**
   * Update user profile
   */
  async updateUserProfile(updates: Partial<UserProfile>): Promise<AuthResult> {
    try {
      if (!this.currentUser) {
        return { success: false, error: 'No user is currently signed in' };
      }

      // Update Firebase Auth profile
      if (updates.displayName || updates.photoURL) {
        await updateProfile(this.currentUser, {
          displayName: updates.displayName,
          photoURL: updates.photoURL
        });
      }

      // Update Firestore profile
      const userRef = doc(db.value, 'users', this.currentUser.uid);
      await updateDoc(userRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      monitoring.trackEvent('profile_updated', { userId: this.currentUser.uid });
      
      return { success: true, user: this.currentUser };
    } catch (error) {
      const authError = error as AuthError;
      return { 
        success: false, 
        error: this.getErrorMessage(authError.code),
        code: authError.code 
      };
    }
  }

  /**
   * Get user profile from Firestore.
   *
   * A profile that does not exist is `null`. A read that FAILED is not.
   *
   * FIXED 2026-09-10 (live service suite, LAUNCH_PLAN.md task 12). The catch below
   * returned `null` for every failure, so "this read was refused" and "this account has
   * no profile" reached the caller as the same answer — CLAUDE.md rule 8b, the most
   * expensive translation in this codebase. `getDoc` resolves with `exists() === false`
   * for a missing document and never throws for one, so the only thing that swallow ever
   * absorbed was a real failure: a denied read (`firestore.rules:195` permits the owner
   * only, and a shared classroom Chromebook runs many accounts through one browser) or
   * an unreachable backend.
   */
  async getUserProfile(uid: string): Promise<UserProfile | null> {
    try {
      const userRef = doc(db.value, 'users', uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        return userSnap.data() as UserProfile;
      }

      return null;
    } catch (error) {
      logger.error('Error fetching user profile:', error);
      throw new Error(`Failed to load user profile: ${getErrorMessage(error)}`);
    }
  }


  /**
   * Update last login timestamp
   */
  private async updateLastLogin(uid: string): Promise<void> {
    try {
      const userRef = doc(db.value, 'users', uid);
      // Check document exists before updating — onAuthStateChanged fires
      // immediately on register, potentially before createUserProfile completes.
      // If the doc doesn't exist yet we silently skip; the profile will be
      // updated once createUserProfile finishes.
      const snap = await getDoc(userRef);
      if (!snap.exists()) return;
      await setDoc(userRef, { lastLoginAt: serverTimestamp() }, { merge: true });
    } catch (error) {
      console.error('Error updating last login:', error);
    }
  }

  /**
   * Validate email format
   */
  private validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate password strength
   */
  private validatePassword(password: string): boolean {
    // At least 8 characters, one uppercase, one lowercase, one number
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    return passwordRegex.test(password);
  }

  /**
   * Get user-friendly error message
   */
  private getErrorMessage(code: string): string {
    const errorMessages: Record<string, string> = {
      'auth/email-already-in-use': 'This email is already registered. Please sign in or use a different email.',
      'auth/invalid-email': 'Please enter a valid email address.',
      'auth/operation-not-allowed': 'This operation is not allowed. Please contact support.',
      'auth/weak-password': 'Password is too weak. Please use at least 8 characters with mixed case and numbers.',
      'auth/user-disabled': 'This account has been disabled. Please contact support.',
      'auth/user-not-found': 'No account found with this email. Please check or register.',
      'auth/wrong-password': 'Incorrect password. Please try again or reset your password.',
      'auth/invalid-credential': 'Invalid email or password. Please try again.',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
      'auth/network-request-failed': 'Network error. Please check your connection and try again.',
      'auth/requires-recent-login': 'Please sign in again to complete this action.',
      'auth/invalid-verification-code': 'Invalid verification code. Please try again.',
      'auth/invalid-verification-id': 'Invalid verification ID. Please try again.'
    };

    return errorMessages[code] || 'An unexpected error occurred. Please try again.';
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  /**
   * Resend email verification
   */
  async resendEmailVerification(): Promise<AuthResult> {
    try {
      if (!this.currentUser) {
        return { success: false, error: 'No user is currently signed in' };
      }

      await sendEmailVerification(this.currentUser);
      return { success: true };
    } catch (error) {
      const authError = error as AuthError;
      return { 
        success: false, 
        error: this.getErrorMessage(authError.code),
        code: authError.code 
      };
    }
  }

  /**
   * Request deletion of the signed-in account.
   *
   * REWIRED 2026-09-05. This used to mark `users/{uid}.deleted = true` and then
   * delete the Firebase Auth account from the browser, leaving every project,
   * submission, chat message, assessment result and skill record behind. Almost
   * every Firestore rule keys on `request.auth.uid == resource.data.userId`, so
   * once the auth account was gone that data could never be read or deleted
   * again — by the user, by a teacher, or by an admin. A child's records became
   * permanently orphaned AND permanently undeletable, which under FERPA is the
   * wrong outcome twice over. The code even said so: "in production, this
   * should trigger a Cloud Function to clean up all user data".
   *
   * That Cloud Function existed the whole time. `cascadeDeleteUserData` fires
   * when a `scheduledDeletions/{id}` document expires — and nothing anywhere
   * wrote one, so the cascade was unreachable.
   *
   * The `deleteMyAccount` callable now disables the account immediately, marks
   * the profile, and schedules the purge 30 days out. Re-authentication is
   * still required here first: this button is most likely to be pressed by a
   * ten-year-old, and a password prompt is the last check before an account
   * stops working.
   */
  async deleteAccount(password?: string): Promise<AuthResult> {
    try {
      if (!this.currentUser) {
        return { success: false, error: 'No user is currently signed in' };
      }

      // S50: re-authentication used to be unconditionally
      // `EmailAuthProvider.credential(email, password)`. A Google, Microsoft or
      // Clever account has NO password, so that credential could never verify and
      // deletion was impossible for every federated account — while the UI still
      // prompted for a password they do not have and showed them "wrong password".
      //
      // COPPA 16 CFR 312.6 gives a parent the right to have their child's data
      // deleted, and a district will ask to see it work. Re-authentication now uses
      // whatever the account actually signed in with.
      const providers = (this.currentUser.providerData ?? []).map((p) => p?.providerId);
      const hasPassword = providers.includes('password');

      if (hasPassword) {
        if (!this.currentUser.email) {
          return { success: false, error: 'No user is currently signed in' };
        }
        if (!password) {
          return { success: false, error: 'Your password is required to delete your account.' };
        }
        await reauthenticateWithCredential(
          this.currentUser,
          EmailAuthProvider.credential(this.currentUser.email, password)
        );
      } else if (providers.includes('google.com')) {
        await reauthenticateWithPopup(this.currentUser, new GoogleAuthProvider());
      } else if (providers.includes('microsoft.com')) {
        await reauthenticateWithPopup(this.currentUser, new OAuthProvider('microsoft.com'));
      } else {
        // A custom-token account — Clever and LTI sign in with a token minted by a
        // Cloud Function, and Firebase offers no re-authentication path for those.
        // Deletion still requires a valid session and the `deleteMyAccount` callable
        // authorizes the caller, so this proceeds rather than locking a rostered
        // child out of their own deletion right. Recorded in SECURITY_TODO S50.
        logger.warn(
          'Account deletion without re-authentication: provider offers no reauth path',
          { providers }
        );
      }

      const uid = this.currentUser.uid;
      await callFunction('deleteMyAccount', { reason: 'user requested account deletion' });

      monitoring.trackEvent('account_deleted', { userId: uid });

      // The account is disabled server-side; end the local session too so the
      // UI cannot keep acting as a signed-in user.
      await signOut(auth.value);
      sessionSecurity.secureLogout();

      return { success: true };
    } catch (error) {
      const authError = error as AuthError;
      return {
        success: false,
        error: this.getErrorMessage(authError.code),
        code: authError.code
      };
    }
  }
}

// Export singleton instance
export const authService = new AuthService();

// Export types
export type { AuthService };